import { useEffect, useId, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";

import {
  landingPlatformAntiBotting,
  landingPlatformBrandSafety,
  landingPlatformInsights,
  landingPlatformVerification,
  landingSeverityHigh,
  landingSeverityLow,
  landingSeverityMedium,
} from "@/lib/assets";
import { resolveAnalyticsElement } from "@/lib/analytics/analyticsElement";
import type { AnalyticsElementId } from "@/lib/analytics/analyticsElementId";
import { deriveAnalyticsElementId } from "@/lib/analytics/deriveAnalyticsElementId";
import { InternalLink } from "@/shared/ui/internal-link";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { articleRegistry } from "@/features/blog/articleRegistry";

import "@/features/blog/blog.css";
import "./product-features.css";

/** The published article a tab links to, so the link follows the registry's title and path. */
const articleAt = (slug: string) => articleRegistry.find((article) => article.slug === slug);

/** The section heading most tabs share; a tab that pitches something else brings its own. */
const defaultHeading = {
  lines: ["Know what’s running.", "Know what you’re paying for."],
  intro:
    "Set the rules, follow delivery and understand the results — with campaign controls and reporting built for professional media buyers.",
};

const features = [
  {
    value: "verification",
    analyticsSuffix: "view-verification",
    tabLabel: "View Verification",
    title: "View Verification",
    heading: defaultHeading,
    paragraphs: [
      "Evangelist goes beyond screenshots which can be doctored in minutes, generated by a modified app, or produced by fake replicas of platform analytics pages.",
      "We close the gap with zero-knowledge cryptographic verification, built with a specialized technology partner. Demographics, views, and engagements are verified as they arrive from the platform’s own servers.",
    ],
    severities: [],
    preview: { src: landingPlatformVerification, width: 568, height: 247 },
    previewAlt:
      "Reporting view narrowing total views to paid views, with botted and non-audience views removed at each step",
    article: undefined,
  },
  {
    value: "antibotting",
    analyticsSuffix: "anti-botting",
    tabLabel: "Anti-botting",
    title: "Anti-Botting",
    heading: defaultHeading,
    paragraphs: [
      "We use particle-physics-grade detection. We watch the whole lifetime of a post, not just a snapshot. Views, likes, comments, and channel statistics are tracked with high granularity from the moment a clip goes live. Botted growth doesn't just inflate total numbers — it evolves differently than organic growth, and those differences only become measurable when the full time series is recorded.",
    ],
    severities: [],
    preview: { src: landingPlatformAntiBotting, width: 595, height: 304 },
    previewAlt:
      "Reporting view showing verified views, the share of botted clips, and botted views per day by creator platform",
    article: articleAt("antibotting"),
  },
  {
    value: "safety",
    analyticsSuffix: "brand-safety",
    tabLabel: "Brand Safety",
    title: "Brand Safety",
    heading: defaultHeading,
    paragraphs: [
      "Every clip is analyzed frame by frame including audio and metadata aligned to IAB brand safety floor and campaign-defined rules. Videos are screened by multimodal & video intelligence AI and scored based on compliance.",
      "Three levels of violations exist with automated responses:",
    ],
    severities: [
      { icon: landingSeverityLow, label: "Low", description: "Demonetized" },
      { icon: landingSeverityMedium, label: "Medium", description: "Removal Requested" },
      { icon: landingSeverityHigh, label: "High", description: "Removed & Suspended" },
    ],
    preview: { src: landingPlatformBrandSafety, width: 348, height: 304 },
    previewAlt:
      "Reporting view of a flagged clip: an automatic sensitive-content check above the incident's type, severity and status",
    article: articleAt("brand-safety"),
  },
  {
    value: "insights",
    analyticsSuffix: "campaign-insights",
    tabLabel: "Campaign Insights",
    title: "Campaign Insights",
    heading: {
      lines: ["Know who is seeing your content.", "Know what you are paying for."],
      intro:
        "Follow delivery and understand the results beyond just reach with campaign controls and insights built for the enterprise media buyer.",
    },
    paragraphs: [
      "The Evangelist platform provides industry leading insights via real-time dashboards across all the metrics that matter.",
      "Reach, Fraud, Engagement, Sentiment, Content Insights, and even Demographic delivery are all provided across platform and at the clip-level.",
    ],
    severities: [],
    preview: { src: landingPlatformInsights, width: 533, height: 304 },
    previewAlt: "Reporting view breaking campaign delivery down by content theme and by country",
    article: undefined,
  },
] as const;

type ProductFeaturesProps = {
  baseAnalyticsElementId: AnalyticsElementId;
};

export function ProductFeatures({ baseAnalyticsElementId }: ProductFeaturesProps) {
  const headingId = useId();
  const tabListRef = useRef<HTMLDivElement>(null);
  const [selectedFeature, setSelectedFeature] = useState<string>(features[0].value);
  const { heading } = features.find((feature) => feature.value === selectedFeature) ?? features[0];

  useEffect(() => {
    const list = tabListRef.current;
    if (!list) return;

    const revealActiveTab = () => {
      const activeTab = list.querySelector<HTMLElement>('[data-state="active"]');
      if (!activeTab || list.scrollWidth <= list.clientWidth) return;

      const listBounds = list.getBoundingClientRect();
      const tabBounds = activeTab.getBoundingClientRect();
      // Move only the horizontal rail. scrollIntoView would also move the page vertically.
      if (tabBounds.left < listBounds.left) {
        list.scrollLeft += tabBounds.left - listBounds.left;
      } else if (tabBounds.right > listBounds.right) {
        list.scrollLeft += tabBounds.right - listBounds.right;
      }
    };

    revealActiveTab();
    const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(revealActiveTab);
    observer?.observe(list);
    return () => observer?.disconnect();
  }, [selectedFeature]);

  const selectFeature = (value: string) => {
    const feature = features.find((item) => item.value === value);
    if (!feature || value === selectedFeature) return;

    setSelectedFeature(value);
    // Radix owns pointer and automatic keyboard selection. Emitting at this single boundary
    // avoids counting both the pointer focus and click, or rendering the initial panel.
    resolveAnalyticsElement(deriveAnalyticsElementId(baseAnalyticsElementId, feature.analyticsSuffix))?.emitClick();
  };

  return (
    <section id="solutions" className="landing-product-features" aria-labelledby={headingId}>
      <header className="landing-product-features__header">
        <p className="landing-product-features__eyebrow">The Evangelist platform</p>
        <h2 id={headingId}>
          {heading.lines[0]}
          <br />
          {heading.lines[1]}
        </h2>
        <p className="landing-product-features__intro">{heading.intro}</p>
      </header>

      <Tabs value={selectedFeature} onValueChange={selectFeature} className="landing-product-features__tabs">
        <TabsList ref={tabListRef} aria-label="Platform capabilities" className="landing-product-features__tab-list">
          {features.map((feature) => {
            const analyticsElement = resolveAnalyticsElement(
              deriveAnalyticsElementId(baseAnalyticsElementId, feature.analyticsSuffix),
            );
            return (
              <TabsTrigger
                key={feature.value}
                value={feature.value}
                className="landing-product-features__tab"
                {...analyticsElement?.attributes}
              >
                {feature.tabLabel}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {features.map(({ value, analyticsSuffix, title, paragraphs, severities, preview, previewAlt, article }) => (
          <TabsContent key={value} value={value} className="landing-product-features__panel">
            <div className="landing-product-features__copy">
              <h3>{title}</h3>
              {paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              {severities.length > 0 && (
                <ul className="landing-product-features__severities">
                  {severities.map(({ icon, label, description }) => (
                    <li key={label}>
                      <span className="landing-product-features__severity-tile">
                        <img src={icon} alt="" width={32} height={32} aria-hidden="true" />
                      </span>
                      <span>
                        <strong>{label}</strong>
                        {description}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {article && (
                <InternalLink
                  analyticsElementId={deriveAnalyticsElementId(baseAnalyticsElementId, `${analyticsSuffix}-article`)}
                  href={article.path}
                  aria-label={`Read Article: ${article.title}`}
                  className="blog-article-link"
                >
                  Read Article <ArrowRight size={20} aria-hidden="true" />
                </InternalLink>
              )}
            </div>
            <figure className="landing-product-features__preview">
              {/* The panel is only rendered for the active tab, so the visible screenshot is
                  never lazy; the other three never mount. Each export is 2x, with the cards'
                  shadows baked into its margin, so it draws at half its pixel size. */}
              <img src={preview.src} alt={previewAlt} width={preview.width} height={preview.height} decoding="async" />
              <figcaption>Illustrative data</figcaption>
            </figure>
          </TabsContent>
        ))}
      </Tabs>
    </section>
  );
}
